// MobileAddictionLab.jsx
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import MobiMascot from "../../../../asstes/habits/mobi_mascot.jpg";
import MindImg from "../../../../asstes/habits/mind.png";
import StudiesImg from "../../../../asstes/habits/studies.png";
import FriendsImg from "../../../../asstes/habits/dinner.png";
import HealthImg from "../../../../asstes/habits/eyes.jpg";

export default function MobileAddictionLab() {
  const { t } = useTranslation("habits");

  // slides (array of {id, label})
  const SLIDES = t("mobile.slides", { returnObjects: true }) || [
    { id: 0, label: "Story Intro" },
    { id: 1, label: "Effects" },
    { id: 2, label: "Day Timeline" },
    { id: 3, label: "Healthy Habits" },
  ];

  // steps as array of objects with id,label,title,description,example,emoji
  const STEPS = [
    {
      id: "health",
      label: t("mobile.steps.0.label"),
      title: t("mobile.steps.0.title"),
      description: t("mobile.steps.0.description"),
      example: t("mobile.steps.0.example"),
      emoji: "😵‍💫",
      img: HealthImg || null,
    },
    {
      id: "mind",
      label: t("mobile.steps.1.label"),
      title: t("mobile.steps.1.title"),
      description: t("mobile.steps.1.description"),
      example: t("mobile.steps.1.example"),
      emoji: "🤯",
      img: MindImg,
    },
    {
      id: "studies",
      label: t("mobile.steps.2.label"),
      title: t("mobile.steps.2.title"),
      description: t("mobile.steps.2.description"),
      example: t("mobile.steps.2.example"),
      emoji: "📉",
      img: StudiesImg,
    },
    {
      id: "friends",
      label: t("mobile.steps.3.label"),
      title: t("mobile.steps.3.title"),
      description: t("mobile.steps.3.description"),
      example: t("mobile.steps.3.example"),
      emoji: "🙈",
      img: FriendsImg, // dinner.png
    },
  ];

  // timeline array
  const rawTimeline = t("mobile.timeline", { returnObjects: true });
  const TIMELINE = Array.isArray(rawTimeline)
    ? rawTimeline
    : [
        {
          time: "6:30 AM",
          title: "Wake-up scroll",
          text: "Checks messages and reels in bed instead of getting ready.",
          emoji: "⏰📱",
        },
        {
          time: "8:00 AM",
          title: "Travel gaming",
          text: "On the way to school, keeps playing games instead of looking outside.",
          emoji: "🚌🎮",
        },
        {
          time: "1:00 PM",
          title: "Lunch-time screen",
          text: "Uses the phone during lunch instead of chatting with classmates.",
          emoji: "🍱📲",
        },
        {
          time: "6:00 PM",
          title: "Study time distraction",
          text: "Phone keeps buzzing. Homework is slow and half-done.",
          emoji: "📚💤",
        },
        {
          time: "11:30 PM",
          title: "Late-night reels",
          text: "Sleeps late and feels very tired next morning.",
          emoji: "🌙😴",
        },
      ];

  // Good habits and bad habits arrays
  const rawGood = t("mobile.goodHabits", { returnObjects: true });
  const GOOD_HABITS = Array.isArray(rawGood)
    ? rawGood
    : [
        "No phone 1 hour before sleep",
        "Keep phone outside study table",
        "Go out to play every day",
        "Talk to family during meals",
        "Fix a screen-time limit with parents",
        "Use phone in open, shared spaces",
      ];

  const rawBad = t("mobile.badHabits", { returnObjects: true });
  const BAD_HABITS_VISUAL = Array.isArray(rawBad)
    ? rawBad
    : [
        "Scrolling in bed",
        "Playing while studying",
        "Phone at dinner",
        "Checking every ping",
      ];

  const INTRO_POINTS = t("mobile.intro_points", { returnObjects: true }) || [
    "Swipe between scenes like levels of a game.",
    "Watch how Mobi affects a child’s whole day.",
    "Learn quick ideas to control screen time.",
    "Earn XP as you explore each part.",
  ];

  const [activeSlide, setActiveSlide] = useState(0);
  const [activeStep, setActiveStep] = useState(STEPS?.[0]?.id || "health");
  const [xp, setXp] = useState(10);

  const currentStep = STEPS.find((s) => s.id === activeStep) || STEPS[0];
  const level = xp < 30 ? 1 : xp < 60 ? 2 : 3;

  const handleSwipeEnd = (_, info) => {
    if (info.offset.x < -60 && activeSlide < SLIDES.length - 1) {
      setActiveSlide((prev) => prev + 1);
      setXp((prev) => Math.min(prev + 5, 100));
    } else if (info.offset.x > 60 && activeSlide > 0) {
      setActiveSlide((prev) => prev - 1);
    }
  };

  const goToSlide = (index) => {
    if (index >= 0 && index < SLIDES.length) {
      setActiveSlide(index);
      setXp((prev) => Math.min(prev + 3, 100));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-violet-50 to-rose-50 text-slate-800 flex flex-col relative overflow-hidden">
      {/* Floating background blobs */}
      <motion.div
        className="pointer-events-none absolute -top-16 -left-10 w-40 h-40 rounded-full bg-sky-100"
        animate={{ y: [0, 10, 0], opacity: [0.6, 1, 0.6] }}
        transition={{ repeat: Infinity, duration: 8 }}
      />
      <motion.div
        className="pointer-events-none absolute -bottom-24 -right-10 w-52 h-52 rounded-full bg-pink-100"
        animate={{ y: [0, -10, 0], opacity: [0.6, 1, 0.6] }}
        transition={{ repeat: Infinity, duration: 10 }}
      />

      {/* Top Bar */}
      <header className="w-full border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="hidden md:inline-flex text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 hover:bg-slate-100"
            >
              ← {t("mobile.back")}
            </button>

            <div>
              <h1 className="text-lg md:text-2xl font-bold flex items-center gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-sky-100 text-sky-600">
                  📱
                </span>
                <span className="text-sky-600">{t("mobile.title")}</span>
              </h1>
              <p className="text-[0.7rem] md:text-xs text-slate-500">
                {t("mobile.subtitle")}
              </p>
            </div>
          </div>

          {/* XP */}
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end">
              <div className="flex items-center gap-1 text-[0.7rem] font-semibold text-slate-600">
                <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
                  Level {level}
                </span>
                <span className="flex items-center gap-0.5 text-amber-500">
                  ★ {Math.round(xp / 10)}
                </span>
              </div>
              <div className="mt-1 w-32 h-2 rounded-full bg-slate-100 overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-sky-400 via-violet-400 to-amber-400"
                  animate={{ width: `${xp}%` }}
                  transition={{ type: "spring", stiffness: 80, damping: 20 }}
                />
              </div>
            </div>
            <span className="text-xs md:text-sm font-medium text-slate-500">
              Classes 6–10
            </span>
          </div>
        </div>

        {/* Slide navigation */}
        <div className="border-t border-slate-200 bg-white/80">
          <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
            <div className="flex gap-2 flex-wrap">
              {SLIDES.map((slide, index) => {
                const active = index === activeSlide;
                return (
                  <button
                    key={slide.id}
                    onClick={() => goToSlide(index)}
                    className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1 transition ${
                      active
                        ? "bg-gradient-to-r from-sky-500 to-violet-500 text-white border-sky-500 shadow-sm"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <span className="inline-block w-4 h-4 rounded-full bg-slate-100 flex items-center justify-center text-[0.6rem]">
                      {index + 1}
                    </span>
                    {slide.label}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => goToSlide(activeSlide - 1)}
                disabled={activeSlide === 0}
                className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 disabled:opacity-40"
              >
                ◀
              </button>
              <button
                onClick={() => goToSlide(activeSlide + 1)}
                disabled={activeSlide === SLIDES.length - 1}
                className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 disabled:opacity-40"
              >
                ▶
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-4 md:py-8">
        <div className="relative overflow-hidden rounded-3xl bg-white/70 border border-slate-200 shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
          <motion.div
            className="flex"
            animate={{ x: `-${activeSlide * 100}%` }}
            transition={{ type: "spring", stiffness: 90, damping: 20 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            onDragEnd={handleSwipeEnd}
          >
            {/* SLIDE 0 — INTRO */}
            <section className="min-w-full px-4 md:px-8 py-6 md:py-10 grid md:grid-cols-2 gap-8 items-center">
              <motion.div
                className="flex justify-center"
                animate={{ y: [0, -8, 0] }}
                transition={{ repeat: Infinity, duration: 3.2 }}
              >
                <img
                  src={MobiMascot}
                  alt="Mobi Mascot"
                  className="w-60 h-60 md:w-72 md:h-72 object-contain rounded-3xl shadow-xl"
                />
              </motion.div>

              {/* INTRO TEXT */}
              <div className="space-y-4">
                <h2 className="text-2xl md:text-3xl font-extrabold leading-tight">
                  <span className="bg-gradient-to-r from-sky-500 to-violet-500 bg-clip-text text-transparent">
                    {t("mobile.title")}
                  </span>
                </h2>

                <div className="bg-sky-50/80 border border-sky-100 rounded-2xl p-3 text-xs md:text-sm flex gap-2">
                  <span className="text-xl mt-0.5">📖</span>
                  <p className="text-slate-700">{t("mobile.subtitle")}</p>
                </div>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs md:text-sm">
                  {INTRO_POINTS.map((text, i) => (
                    <li
                      key={i}
                      className="bg-violet-50/60 border border-violet-100 rounded-xl px-3 py-2 flex items-start gap-2"
                    >
                      <span className="mt-0.5">✨</span>
                      <span className="text-slate-700">{text}</span>
                    </li>
                  ))}
                </ul>

                <div className="pt-1 flex gap-3 items-center">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => goToSlide(1)}
                    className="px-5 py-2.5 rounded-full bg-gradient-to-r from-sky-500 to-violet-500 text-white text-sm md:text-base font-semibold shadow-md"
                  >
                    {t("mobile.startLevel")}
                  </motion.button>
                  <p className="text-[0.7rem] text-slate-500">
                    {t("mobile.tip")}
                  </p>
                </div>
              </div>
            </section>

            {/* SLIDE 1 — EFFECTS (STEPS) */}
            <section className="min-w-full px-4 md:px-8 py-6 md:py-10 space-y-6">
              <h3 className="text-xl md:text-2xl font-bold flex items-center gap-2">
                <span className="inline-flex h-7 w-7 rounded-full bg-rose-100 text-rose-500">
                  ⚠️
                </span>
                {t("mobile.level1_title")}
              </h3>

              {/* Step Buttons */}
              <div className="flex flex-wrap gap-2">
                {STEPS.map((step) => {
                  const selected = step.id === activeStep;
                  return (
                    <motion.button
                      key={step.id}
                      whileHover={{ scale: 1.05 }}
                      onClick={() => setActiveStep(step.id)}
                      className={`px-4 py-1.5 rounded-full text-xs md:text-sm border flex items-center gap-2 ${
                        selected
                          ? "bg-gradient-to-r from-rose-400 to-orange-400 text-white"
                          : "bg-white text-slate-700 border-slate-200"
                      }`}
                    >
                      <span>{step.emoji}</span>
                      {step.label}
                    </motion.button>
                  );
                })}
              </div>

              {/* Step Content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.25 }}
                  className="grid md:grid-cols-3 gap-5"
                >
                  {/* LEFT IMAGE */}
                  <div className="bg-gradient-to-b from-rose-50 to-amber-50 rounded-2xl border p-4 flex flex-col">
                    <p className="text-[0.7rem] font-semibold text-rose-500 uppercase">
                      {t("mobile.story_scene")}
                    </p>

                    <div className="flex-1 flex items-center justify-center my-3">
                      <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden bg-white shadow">
                        <motion.img
                          key={currentStep.id}
                          src={
                            currentStep.id === "health"
                              ? HealthImg
                              : currentStep.id === "mind"
                              ? MindImg
                              : currentStep.id === "studies"
                              ? StudiesImg
                              : FriendsImg
                          }
                          alt={currentStep.title}
                          className="w-full h-full object-cover"
                          initial={{ opacity: 0, scale: 1.05 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.5 }}
                        />
                      </div>
                    </div>

                    <p className="text-[0.7rem] text-slate-500">
                      {t("mobile.mini_story")}
                    </p>
                  </div>

                  {/* RIGHT TEXT */}
                  <div className="md:col-span-2 bg-white rounded-2xl border p-5 flex flex-col justify-between">
                    <div className="space-y-2">
                      <h4 className="text-lg font-semibold flex items-center gap-2">
                        <span className="text-xl">{currentStep.emoji}</span>
                        <span>{currentStep.title}</span>
                      </h4>
                      <p className="text-sm text-slate-600">
                        {currentStep.description}
                      </p>
                    </div>

                    <div className="mt-3 border-t pt-3 space-y-1">
                      <p className="text-[0.7rem] font-semibold text-slate-500">
                        Mini-story
                      </p>
                      <p className="text-sm text-slate-700 italic">
                        {currentStep.example}
                      </p>
                      <p className="text-[0.7rem] text-slate-500">
                        {t("mobile.class_question")}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </section>

            {/* SLIDE 2 — TIMELINE */}
            <section className="min-w-full px-4 md:px-8 py-6 md:py-10 space-y-5">
              <h3 className="text-xl md:text-2xl font-bold flex items-center gap-2">
                <span className="inline-flex h-7 w-7 rounded-full bg-sky-100 text-sky-500">
                  📅
                </span>
                {t("mobile.level2_title")}
              </h3>

              <p className="text-sm text-slate-600">
                {t("mobile.timeline_caption")}
              </p>

              <div className="relative pl-8 max-h-[26rem] overflow-y-auto pr-2 pb-2">
                <div className="absolute top-0 left-2 w-[3px] h-full bg-gradient-to-b from-sky-300 via-violet-300 to-rose-300 rounded-full" />

                <div className="space-y-4">
                  {TIMELINE.map((item, index) => (
                    <motion.div
                      key={item.time + index}
                      initial={{ opacity: 0, x: -15 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, amount: 0.4 }}
                      transition={{ delay: index * 0.05 }}
                      className="relative pl-4"
                    >
                      <div className="absolute left-[-10px] top-3 w-5 h-5 rounded-full bg-white shadow-md flex items-center justify-center border text-xs">
                        {index + 1}
                      </div>

                      <div className="bg-white rounded-2xl shadow-sm p-4 border">
                        <div className="flex justify-between flex-wrap">
                          <span className="text-[0.7rem] font-semibold text-sky-500 flex items-center gap-1">
                            {item.time}{" "}
                            <span className="text-xs">{item.emoji}</span>
                          </span>
                          <h4 className="text-sm md:text-base font-semibold text-slate-800">
                            {item.title}
                          </h4>
                        </div>
                        <p className="mt-1 text-xs md:text-sm text-slate-600">
                          {item.text}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              <p className="text-[0.7rem] text-slate-500">
                {t("mobile.timeline_prompt")}
              </p>
            </section>

            {/* SLIDE 3 — HEALTHY HABITS */}
            <section className="min-w-full px-4 md:px-8 py-6 md:py-10 space-y-6">
              <h3 className="text-xl md:text-2xl font-bold flex items-center gap-2">
                <span className="inline-flex h-7 w-7 rounded-full bg-emerald-100 text-emerald-500">
                  🌱
                </span>
                {t("mobile.level3_title")}
              </h3>

              <p className="text-sm text-slate-600">
                {t("mobile.level3_desc")}
              </p>

              <div className="grid lg:grid-cols-3 gap-6 items-start">
                {/* Good Habits */}
                <div className="lg:col-span-2 space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
                    {GOOD_HABITS.map((habit, i) => (
                      <motion.div
                        key={habit + i}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ delay: i * 0.03 }}
                        whileHover={{ scale: 1.04 }}
                        onClick={() => setXp((prev) => Math.min(prev + 4, 100))}
                        className="bg-gradient-to-br from-emerald-50 to-sky-50 rounded-2xl shadow-sm p-4 border flex items-start gap-2 cursor-pointer"
                      >
                        <span className="inline-flex h-5 w-5 rounded-full bg-emerald-100 text-emerald-600 text-xs items-center justify-center">
                          ✔
                        </span>
                        <div>
                          <p className="text-xs md:text-sm text-slate-700">
                            {habit}
                          </p>
                          {/* <p className="mt-1 text-[0.65rem] text-slate-500">
                          Tap to imagine adding this habit to your own day.
                        </p> */}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Bad Habits Drag Zone */}
                <div className="bg-gradient-to-b from-sky-50 via-violet-50 to-rose-50 rounded-2xl shadow-sm p-4 border flex flex-col gap-3">
                  <p className="text-[0.7rem] font-semibold text-sky-600 flex items-center gap-1">
                    🎯 {t("mobile.mini_activity")}
                  </p>

                  <p className="text-xs text-slate-600">
                    {t("mobile.healthy_zone_instruction")}
                  </p>

                  <div className="relative h-60 bg-white/70 border rounded-2xl overflow-hidden">
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center"
                      animate={{ scale: [0.96, 1.02, 0.96] }}
                      transition={{ duration: 3, repeat: Infinity }}
                    >
                      <div className="w-32 h-32 rounded-full bg-sky-100 border flex flex-col items-center justify-center text-center text-[0.65rem] text-sky-700 px-3">
                        <div>💙</div>
                        <div className="text-[0.7rem]">
                          {t("mobile.healthy_zone_title")}
                        </div>
                        <div className="text-[0.65rem]">
                          {t("mobile.healthy_zone_sub")}
                        </div>
                      </div>
                    </motion.div>

                    {BAD_HABITS_VISUAL.map((bad, index) => (
                      <motion.div
                        key={bad + index}
                        drag
                        dragSnapToOrigin
                        whileDrag={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
                        className="absolute px-3 py-1.5 rounded-full bg-gradient-to-r from-rose-400 to-orange-400 text-white text-[0.65rem] shadow-md cursor-grab"
                        style={{
                          top: 16 + index * 34,
                          right: 10 + (index % 2) * 20,
                        }}
                      >
                        {bad}
                      </motion.div>
                    ))}
                  </div>

                  <p className="text-[0.7rem] text-slate-500">
                    {t("mobile.bad_habits_caption")}
                  </p>
                </div>
              </div>
            </section>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
